import { supabase } from "@/integrations/supabase/client";
import { Template } from "@/lib/types";

export const fetchTemplates = async (tenantId: string): Promise<Template[]> => {
  // Fetch templates
  const { data: templates, error: templatesError } = await supabase
    .from("templates")
    .select("*")
    .eq("tenant_id", tenantId)
    .order("updated_at", { ascending: false });

  if (templatesError) {
    console.error("Error fetching templates:", templatesError);
    throw templatesError;
  }

  if (!templates) return [];

  // For each template, count its usage in rules
  const templatesWithUsage = await Promise.all(
    templates.map(async (template) => {
      const { count, error: countError } = await supabase
        .from("rules")
        .select("*", { count: "exact", head: true })
        .eq("tenant_id", tenantId)
        .filter(
          "actions",
          "cs",
          JSON.stringify([{ type: "reply", value: { templateId: template.id } }])
        );

      if (countError) {
        console.error("Error counting template usage:", countError);
        // Return 0 on error rather than failing completely
        return { ...template, usage_count: 0 };
      }

      return { ...template, usage_count: count || 0 };
    })
  );

  return templatesWithUsage;
};

export const fetchTemplateById = async (id: string): Promise<Template | null> => {
  const { data, error } = await supabase
    .from("templates")
    .select("*")
    .eq("id", id)
    .maybeSingle();

  if (error) {
    console.error("Error fetching template:", error);
    throw error;
  }

  if (!data) return null;

  // Calculate usage count for this template
  const { count, error: countError } = await supabase
    .from("rules")
    .select("*", { count: "exact", head: true })
    .eq("tenant_id", data.tenant_id)
    .filter(
      "actions",
      "cs",
      JSON.stringify([{ type: "reply", value: { templateId: id } }])
    );

  if (countError) {
    console.error("Error counting template usage:", countError);
    return { ...data, usage_count: 0 };
  }

  return { ...data, usage_count: count || 0 };
};

export const createTemplate = async (template: Omit<Template, 'id' | 'created_at' | 'updated_at' | 'usage_count'>): Promise<Template> => {
  const { data, error } = await supabase
    .from("templates")
    .insert(template)
    .select()
    .single();

  if (error) {
    console.error("Error creating template:", error);
    throw error;
  }

  // New templates have 0 usage count
  return { ...data, usage_count: 0 };
};

export const updateTemplate = async (id: string, template: Partial<Template>): Promise<Template> => {
  // Remove usage_count from update data since it's calculated dynamically
  const { usage_count, ...updateData } = template;
  
  const { data, error } = await supabase
    .from("templates")
    .update(updateData)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating template:", error);
    throw error;
  }

  // Calculate current usage count
  const { count, error: countError } = await supabase
    .from("rules")
    .select("*", { count: "exact", head: true })
    .eq("tenant_id", data.tenant_id)
    .filter(
      "actions",
      "cs",
      JSON.stringify([{ type: "reply", value: { templateId: id } }])
    );

  if (countError) {
    console.error("Error counting template usage:", countError);
    return { ...data, usage_count: 0 };
  }

  return { ...data, usage_count: count || 0 };
};

export const deleteTemplate = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from("templates")
    .delete()
    .eq("id", id);

  if (error) {
    console.error("Error deleting template:", error);
    throw error;
  }
};
